import { useMemo } from "react";

import {
  alpha,
  Box,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";

import { Doughnut } from "react-chartjs-2";

import {
  findObjectKeyByValue,
  formatNumberCustomPattern,
  titleCase,
} from "../../utils/helper";

import { LEVEL_OF_HEARING_LOSS } from "../../utils/beneficiary.constant";

ChartJS.register(ArcElement, Tooltip, Legend);

const HEARING_LOSS_COLORS = {
  1: "#2A137B",
  2: "#1F63D3",
  3: "#22B6C7",
  4: "#8BCB47",
  5: "#F2C316",
  6: "#FF7A1A",
  7: "#E046A7",
};

const getHearingLossLabel = (value) => {
  const key = findObjectKeyByValue(Number(value), LEVEL_OF_HEARING_LOSS);

  return key
    ? titleCase(key.replaceAll("_", " ").toLowerCase())
    : `Category ${value}`;
};

const HearingLossChart = ({
  hearingLossCounts = {},
  totalBeneficiaries = 0,
  loading = false,
}) => {

  const chartItems = useMemo(() => {
    return Object.entries(hearingLossCounts)
      .filter(([key, value]) => Number(key) !== 8 && Number(value) > 0)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([key, value]) => ({
        key,
        value: Number(value),
        label: getHearingLossLabel(key),
        color: HEARING_LOSS_COLORS[key],
      }));
  }, [hearingLossCounts]);

  // =====================================================

  const actualTotal = chartItems.reduce((acc, item) => acc + item.value, 0);

  const isSingleItem = chartItems.length === 1;

  const chartData = {
    labels: chartItems.map((item) => item.label),

    datasets: [
      {
        data: chartItems.map((item) => item.value),

        backgroundColor: chartItems.map((item) => item.color),

        borderColor: "#FFFFFF",

        borderWidth: isSingleItem ? 0 : 5,

        hoverBorderWidth: isSingleItem ? 0 : 5,

        hoverOffset: isSingleItem ? 0 : 4,

        spacing: isSingleItem ? 0 : 2,
      },
    ],
  };

  if (loading) {
    return (
      <Paper
        sx={{
          p: 4,
          borderRadius: "30px",
        }}
      >
        Loading...
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3,

        p: {
          xs: 2,
          md: 3,
        },

        borderRadius: "34px",

        background: "linear-gradient(180deg, #F8FAFF 0%, #FFFFFF 100%)",

        border: "1px solid #E7EDFA",

        overflow: "hidden",

        boxShadow: "0px 10px 40px rgba(16, 45, 120, 0.08)",
      }}
    >
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        mb={4}
      >
        <Grid item xs>
          <Typography
            sx={{
              fontWeight: 800,
              lineHeight: 1,
              color: "#081B4B",
            }}
          >
            Hearing Wise Distribution
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              height: "100%",

              p: 2,

              display: "flex",

              alignItems: "center",

              justifyContent: "center",

              borderRadius: "32px",

              border: "1px solid #EEF2FA",

              background: "#FFFFFF",

              minHeight: isSingleItem ? 260 : 360,
            }}
          >
            <Box
              sx={{
                position: "relative",

                height: isSingleItem ? 220 : "80%",

                width: isSingleItem ? 220 : "75%",

                maxWidth: 320,
              }}
            >
              <Doughnut
                data={chartData}
                options={{
                  responsive: true,

                  maintainAspectRatio: false,

                  cutout: isSingleItem ? "72%" : "56%",

                  plugins: {
                    legend: {
                      display: false,
                    },

                    tooltip: {
                      backgroundColor: "#16244C",

                      padding: 14,

                      displayColors: true,

                      callbacks: {
                        label: (context) => {
                          const value = context.parsed;

                          const percentage = (
                            (value / actualTotal) *
                            100
                          ).toFixed(2);

                          return `${context.label}: ${value} (${percentage}%)`;
                        },
                      },
                    },
                    datalabels: {
                      color: "#FFFFFF",

                      font: {
                        size: 18,
                        weight: "700",
                      },

                      formatter: (value) => value,
                    },
                  },
                }}
              />

              {/* CENTER */}

              <Box
                sx={{
                  position: "absolute",

                  top: "50%",

                  left: "50%",

                  transform: "translate(-50%, -50%)",

                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 15,

                    fontWeight: 500,

                    color: "#667596",
                  }}
                >
                  Total
                </Typography>

                <Typography
                  sx={{
                    fontSize: isSingleItem ? 34 : 20,

                    lineHeight: 1,

                    fontWeight: 800,

                    color: "#081B4B",
                  }}
                >
                  {formatNumberCustomPattern(actualTotal)}
                </Typography>

                <Typography
                  sx={{
                    mt: 1,

                    fontSize: 14,

                    fontWeight: 500,

                    color: "#7584A4",
                  }}
                >
                  Assessments
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{
              height: "100%",

              p: 3,

              borderRadius: "32px",

              border: "1px solid #EEF2FA",

              background: "#FFFFFF",

              display: "flex",

              flexDirection: "column",

              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 16,

                fontWeight: 900,

                lineHeight: 1.1,

                color: "#081B4B",

                mb: 4,
              }}
            >
              Hearing Level Breakdown
            </Typography>

            <Stack spacing={isSingleItem ? 2 : 3}>
              {chartItems.map((item) => {
                const percentage = ((item.value / actualTotal) * 100).toFixed(
                  2,
                );

                return (
                  <Box key={item.key}>
                    <Box
                      sx={{
                        display: "flex",

                        alignItems: "center",

                        justifyContent: "space-between",

                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",

                          alignItems: "center",

                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 10,

                            height: 10,
                            mr: 2,
                            borderRadius: "50%",

                            bgcolor: item.color,

                            boxShadow: `0px 0px 12px ${alpha(item.color, 0.5)}`,
                          }}
                        />

                        <Typography
                          sx={{
                            fontSize: 15,

                            fontWeight: 500,

                            color: "#081B4B",
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Box>

                      {/* RIGHT */}

                      <Box
                        sx={{
                          display: "flex",

                          alignItems: "center",

                          gap: 1.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 16,

                            fontWeight: 900,

                            color: item.color,
                          }}
                        >
                          {item.value}
                        </Typography>

                        <Box
                          sx={{
                            minWidth: 90,

                            p: 1,
                            ml: 2,
                            borderRadius: "999px",

                            textAlign: "center",

                            bgcolor: alpha(item.color, 0.12),
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,

                              fontWeight: 800,

                              color: item.color,
                            }}
                          >
                            {percentage}%
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* PROGRESS */}

                    {!isSingleItem && (
                      <LinearProgress
                        variant="determinate"
                        value={Number(percentage)}
                        sx={{
                          height: 10,

                          borderRadius: 999,

                          bgcolor: alpha(item.color, 0.12),

                          "& .MuiLinearProgress-bar": {
                            borderRadius: 999,

                            background: `linear-gradient(90deg, ${item.color} 0%, ${alpha(
                              item.color,
                              0.75,
                            )} 100%)`,
                          },
                        }}
                      />
                    )}

                    {isSingleItem && (
                      <Typography
                        sx={{
                          mt: 1.5,

                          fontSize: 13,

                          fontWeight: 500,

                          color: "#7B88A8",
                        }}
                      >
                        Only one hearing level detected
                      </Typography>
                    )}
                  </Box>
                );
              })}

              {/* TOTAL */}

              <Box
                sx={{
                  mt: 2,

                  p: 2,

                  borderRadius: "20px",

                  background: "#F6F8FF",

                  display: "flex",

                  alignItems: "center",

                  justifyContent: "space-between",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,

                    fontWeight: 600,

                    color: "#5F6C89",
                  }}
                >
                  Total Assessments
                </Typography>

                <Typography
                  sx={{
                    fontSize: 16,

                    fontWeight: 700,
                    mr: 3,

                    color: "#4A39F6",
                  }}
                >
                  {formatNumberCustomPattern(actualTotal)}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default HearingLossChart;
